import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";

interface MusculoskeletalFields {
  // Fall Risk Assessment Tool

  /**
   * The patient's age range.
   *
   * Options:
   * "60 - 69 years (1 point)",
   * "70 -79 years (2 points)",
   * or "greater than or equal to 80 years (3 points)".
   */
  fallRiskAge: string;

  /**
   * Whether the patient has had "one fall within 6 months before admission" (true)
   * or not (false).
   */
  fallHistory: boolean;

  /**
   * AKA "elimination, bowel and urine."
   *
   * Options:
   * "Incontinence (2 points)",
   * "Urgency or frequency (2 points)",
   * or "Urgency/frequency and incontinence (4 points)".
   */
  fallRiskEbu: string;

  /**
   * The number of high fall risk drugs the patient is currently on.
   *
   * Options:
   * "On 1 high fall risk drug (3 points)",
   * "On 2 or more high fall risk drugs (5 points)",
   * or "Sedated procedure within past 24 hours (7 points)".
   */
  fallRiskMedications: string;

  /**
   * AKA "patient care equipment." The number of pieces of equipment that tethers
   * the patient.
   *
   * Options:
   * "One present (1 point)",
   * "Two present (2 points)",
   * or "3 or more present (3 points)".
   */
  fallRiskPce: string; // AKA patient care equipment

  /**
   * The patient's fall risk score based on their mobility.
   *
   * Options:
   * "Requires assistance or supervision for mobility, transfer, or ambulation (2 points)",
   * "Unsteady gait (2 points)", and
   * "Visual or auditory impairment affecting mobility (2 points)".
   */
  fallRiskMobility: string[];

  /**
   * The patient's fall risk score based on their cognitive ability.
   *
   * Options:
   * "Altered awareness of immediate physical environment (1 point)",
   * "Impulsive (2 points)", and
   * "Lack of understanding of one's physical and cognitive limitations (4 points)".
   */
  fallRiskCognition: string[];

  /**
   * The patient's fall score based on the scoring provided by the above options.
   */
  fallScore: number;

  /**
   * The mobility/transfers the patient has.
   *
   * Options: standby assist, one assist, two assists, or max assist.
   */
  mobilityAndTransfers: string;

  /**
   * The assistive devices a patient uses for mobility.
   *
   * Options: gait belt, walker, cane, and trapeze.
   */
  assistiveDevices: string[];

  limitedRangeOfMotion: string;
  deformitiesPresent: string;
}
export type MusculoskeletalFormGroup = FormGroup<ControlsOf<MusculoskeletalFields>>;

export const MusculoskeletalAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createMusculoskeletalFormGroup,
  toPDF: musculoskeletalAssessmentToPDF,
  formToObject: formToMusculoskeletalAssessment,
  initialState: {
    // Refer to the "Johns Hopkins Fall Risk Assessment Tool"
    fallRiskAge: '',
    fallHistory: false,
    // ebu = elimination, bowel and urine
    fallRiskEbu: '',
    fallRiskMedications: '',
    // pce = patient care equipment
    fallRiskPce: '', // one present, two present, or 3 or more present
    fallRiskMobility: [],
    fallRiskCognition: [],
    fallScore: '',
    mobilityAndTransfers: '',
    assistiveDevices: [],
    limitedRangeOfMotion: '',
    deformitiesPresent: '',
  },
};

/**
 * Holds information about the patient's musculoskeletal assessment.
 */
export class MusculoskeletalAssessment {
  fallRiskAge: string;
  fallHistory: boolean;
  fallRiskEbu: string;
  fallRiskMedications: string;
  fallRiskPce: string; // AKA patient care equipment
  fallRiskMobility: string[];
  fallRiskCognition: string[];
  fallScore: number;
  mobilityAndTransfers: string;
  assistiveDevices: string[];
  limitedRangeOfMotion: string;
  deformitiesPresent: string;

  constructor(data: Partial<MusculoskeletalFields>) {
    const is = MusculoskeletalAssessmentHelpers.initialState;
    this.fallRiskAge = data.fallRiskAge || is.fallRiskAge;
    this.fallHistory = data.fallHistory ?? is.fallHistory;
    this.fallRiskEbu = data.fallRiskEbu || is.fallRiskEbu;
    this.fallRiskMedications = data.fallRiskMedications || is.fallRiskMedications;
    this.fallRiskPce = data.fallRiskPce || is.fallRiskPce;
    this.fallRiskMobility = data.fallRiskMobility || is.fallRiskMobility;
    this.fallRiskCognition = data.fallRiskCognition || is.fallRiskCognition;
    this.fallScore = data.fallScore ?? is.fallScore;
    this.mobilityAndTransfers = data.mobilityAndTransfers || is.mobilityAndTransfers;
    this.assistiveDevices = data.assistiveDevices || is.assistiveDevices;
    this.limitedRangeOfMotion = data.limitedRangeOfMotion || is.limitedRangeOfMotion;
    this.deformitiesPresent = data.deformitiesPresent || is.deformitiesPresent;
  }
}


function createMusculoskeletalFormGroup(musculoskeletalAssessment?: MusculoskeletalAssessment) {
  const data = musculoskeletalAssessment || new MusculoskeletalAssessment(MusculoskeletalAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<MusculoskeletalFields>>({
    fallRiskAge: new FormControl<string | null>(data.fallRiskAge),
    fallHistory: new FormControl<boolean | null>(data.fallHistory),
    fallRiskEbu: new FormControl<string | null>(data.fallRiskEbu),
    fallRiskMedications: new FormControl<string | null>(data.fallRiskMedications),
    fallRiskPce: new FormControl<string | null>(data.fallRiskPce),
    fallRiskMobility: new FormControl<string[] | null>(data.fallRiskMobility),
    fallRiskCognition: new FormControl<string[] | null>(data.fallRiskCognition),
    fallScore: new FormControl<number | null>(data.fallScore),
    mobilityAndTransfers: new FormControl<string | null>(data.mobilityAndTransfers),
    assistiveDevices: new FormControl<string[] | null>(data.assistiveDevices),
    limitedRangeOfMotion: new FormControl<string | null>(data.limitedRangeOfMotion),
    deformitiesPresent: new FormControl<string | null>(data.deformitiesPresent),
  });
}

function musculoskeletalAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: MusculoskeletalAssessment,
): void {
  pdf.addSectionTitle("Musculoskeletal");
  pdf.addTwoColumnText("Mobility/Transfers", assessment.mobilityAndTransfers,
    "Assistive Devices", assessment.assistiveDevices.join(", "));
  pdf.currY += 10;
  pdf.pdf.text("Fall Risk:", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Age", assessment.fallRiskAge,
    "Fall History", assessment.fallHistory ? "Yes" : "No");
  pdf.addOneColumnText("Elimination, Bowel, & Urine", assessment.fallRiskEbu);
  pdf.addOneColumnText("Medications", assessment.fallRiskMedications);
  pdf.addOneColumnText("Patient Care Equipment", assessment.fallRiskPce);
  pdf.addOneColumnText("Mobility", assessment.fallRiskMobility.join(", "));
  pdf.addOneColumnText("Cognition", assessment.fallRiskCognition.join(", "));
  pdf.addOneColumnText("Total Fall Risk Score", assessment.fallScore.toString());
  pdf.currY += 10;
  pdf.addOneColumnText("Limited Range of Motion", assessment.limitedRangeOfMotion);
  pdf.addOneColumnText("Deformaties Present", assessment.deformitiesPresent);
}

function formToMusculoskeletalAssessment(fg: MusculoskeletalFormGroup) {
  return new MusculoskeletalAssessment({
    fallRiskAge: fg.controls.fallRiskAge.value!,
    fallHistory: fg.controls.fallHistory.value!,
    fallRiskEbu: fg.controls.fallRiskEbu.value!,
    fallRiskMedications: fg.controls.fallRiskMedications.value!,
    fallRiskPce: fg.controls.fallRiskPce.value!,
    fallRiskMobility: fg.controls.fallRiskMobility.value!,
    fallRiskCognition: fg.controls.fallRiskCognition.value!,
    fallScore: fg.controls.fallScore.value!,
    mobilityAndTransfers: fg.controls.mobilityAndTransfers.value!,
    assistiveDevices: fg.controls.assistiveDevices.value!,
    limitedRangeOfMotion: fg.controls.limitedRangeOfMotion.value!,
    deformitiesPresent: fg.controls.deformitiesPresent.value!,
  });
}